import React, { useRef, useState } from 'react';
import {
    faPaperPlane,
    faPlus,
    faTimes,
    faFileAlt,
} from '@fortawesome/free-solid-svg-icons';

import { alert, AlertType, Button, Input } from '@quyva/shared-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { pdfjs } from 'react-pdf';
import { RealTimeTranscription } from '../transcriptions';
import { validateUploadedFiles } from '../../utils/validateUploadedFiles';

export const ChatActions = ({
    query,
    setQuery,
    handleSubmit,
    uploadedFiles,
    setUploadedFiles,
    isGenerating,
    selectedProductCategory,
    selectedProduct,
    isSmallScreen,
    enableSpeaker,
    setEnableSpeaker,
    activeAnswer,
    enableAudioFeature,
    setActiveAudioId,
}) => {
    const [isListening, setIsListening] = useState(false);
    const buttonRef = useRef(null);
    const fileInputRef = useRef(null);

    const onAddFiles = async (files) => {
        const existing = Array.isArray(uploadedFiles) ? uploadedFiles : [];
        const toAdd = Array.isArray(files) ? files : [];
        // reuse the same validation logic as main Quyva frontend (Chat UI)
        window.pdfjsLib = pdfjs;
        const { errors, validFiles } = await validateUploadedFiles(toAdd, existing);
        if (errors.length > 0) {
            alert(AlertType.ERROR, errors.join('\n'));
            return;
        }

        if (validFiles.length > 0) {
            setUploadedFiles([...existing, ...validFiles]);
        }
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        await onAddFiles(files);
        e.target.value = null;
    };

    const removeFile = (index) => {
        const existing = Array.isArray(uploadedFiles) ? uploadedFiles : [];
        setUploadedFiles(existing.filter((_, i) => i !== index));
    };

    const handleOnTranscription = (transcript) => {
        if (transcript) {
            setQuery(transcript);
        }
    };

    const handleTranscriptionReset = () => {
        setQuery('');
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            buttonRef?.current?.click(); // Programmatically click the button
            setIsListening(false);
        }
    };

    return (
        <div className='w-full'>
            {Array.isArray(uploadedFiles) && uploadedFiles.length > 0 && (
                <div className='mb-2 flex flex-wrap gap-2'>
                    {uploadedFiles.map((f, idx) => (
                        <div
                            key={`${f.name}-${f.size}-${idx}`}
                            className='flex items-center gap-1 bg-backgrounds-secondary text-xs border bg-backgrounds-border rounded-lg px-2 py-1'
                            title={f.name}
                        >
                            <FontAwesomeIcon
                                icon={faFileAlt}
                                className='text-texts-tertiary'
                            />
                            <span className='truncate max-w-[160px]'>
                                {f.name}
                            </span>
                            <button
                                onClick={() => removeFile(idx)}
                                className='text-texts-secondary300 hover:text-texts-red400'
                                title='Remove file'
                                type='button'
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className='flex items-center justify-between'>
                <input
                    ref={fileInputRef}
                    type='file'
                    className='hidden'
                    multiple
                    onChange={handleFileChange}
                />

                <Button
                    text={null}
                    className='w-12 h-12 ml-1 mr-1 bg-backgrounds-primary'
                    hoverText={'Attach files'}
                    disabled={isGenerating}
                    onClick={() => fileInputRef?.current?.click()}
                    icon={faPlus}
                    type={'success'}
                />

                <div className='w-full bg-backgrounds-white border border-1 rounded-xl px-3 py-1'>
                    <Input
                        className='px-2 py-1 w-full border-none focus:ring-0 focus:outline-none'
                        value={query}
                        type='text'
                        placeholder={
                            selectedProductCategory && selectedProduct
                                ? `Ask question here for ${selectedProduct} (${selectedProductCategory})`
                                : `Ask your question here`
                        }
                        onChange={(e) => {
                            setQuery(e.target.value);
                        }}
                        autoFocus={true}
                        onKeyDown={handleKeyDown}
                    />
                </div>

                <div className='flex items-center ml-2'>
                    {enableAudioFeature && (
                        <div
                            className={`ml-1 mr-2 ${
                                isSmallScreen ? 'relative right-0 z-10' : ''
                            }`}
                        >
                            <RealTimeTranscription
                                onTranscription={handleOnTranscription}
                                onTranscriptionReset={handleTranscriptionReset}
                                isListening={isListening}
                                setIsListening={setIsListening}
                                activeAnswer={activeAnswer}
                                enableSpeaker={enableSpeaker}
                                setEnableSpeaker={setEnableSpeaker}
                                setActiveAudioId={setActiveAudioId}
                                isSmallScreen={isSmallScreen}
                            />
                        </div>
                    )}

                    <Button
                        ref={buttonRef}
                        text={!isSmallScreen ? 'Send' : null}
                        className='mx-1'
                        hoverText={
                            query.length === 0
                                ? 'Please type you query'
                                : 'Send your query and get answer'
                        }
                        disabled={query.length === 0 || isGenerating}
                        onClick={async () => {
                            setIsListening(false);
                            await handleSubmit();
                            setQuery('');
                        }}
                        icon={faPaperPlane}
                        type={'success'}
                    />
                </div>
            </div>
        </div>
    );
};
